/** Admin · revenue (GET /admin/stats) — credit-purchase gross by currency + the per-org rollup (0032). */
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CURRENCY_META, type BillingCurrency } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { currencyMinor, inrCompact, usdCompact } from "../../lib/format.js";

interface OrgRollup {
  id: string;
  name: string;
  /** True for our own demo tenants — the API nulls their money, and this page drops the rows. */
  isTest: boolean;
  /** The org's locked billing currency — its revenue is real money only in THIS currency. */
  currency: BillingCurrency;
  /** Gross credit purchases this cycle, in the org currency's MINOR units. */
  revenueMinor: number;
  /** ≈ INR at FX_TO_INR — the rollup axis, never a billed amount. */
  revenueInrEq: number;
  cogsInr: number;
  /** null when the org collected nothing — no revenue, no meaningful margin. */
  marginPct: number | null;
  verified: number;
}

interface CurrencyGross {
  currency: BillingCurrency;
  grossMinor: number;
  purchases: number;
}

/** Cash vs credits (adminStats `money`) — four figures that reconcile, MTD, test orgs excluded. */
interface MoneyBlock {
  /** Same rows as `revenue` by construction — gross of gateway fees, the bank-matching number. */
  cash: { byCurrency: CurrencyGross[]; inrEq: number };
  creditsGrantedFree: {
    byKind: { kind: string; units: number }[];
    units: number;
    adjustmentUnits: number;
    netUnits: number;
    /** Priced at the price_book INR anchor — what the credits WOULD have sold for. Never cash. */
    notionalInr: number;
    inrAnchorPerCredit: number;
  };
  creditsBurned: { spendUnits: number; expiryUnits: number };
  /** Cash collected for credits not yet consumed, pro-rata over paid lots. */
  deferredRevenue: { byCurrency: { currency: BillingCurrency; deferredMinor: number }[]; inrEq: number };
}

interface StatsResponse {
  stats: {
    /** 0032: revenue = captured credit purchases (credit_purchases, status='paid'), per currency + ₹-eq total. */
    revenue: {
      byCurrency: CurrencyGross[];
      inrEq: number;
    };
    /** Optional: web and API deploy separately, so an older API must not crash this page. */
    money?: MoneyBlock;
    orgs: OrgRollup[];
  };
}

export function Revenue(): ReactNode {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get<StatsResponse>("/admin/stats"),
  });
  const stats = data?.stats;
  // The disclaimer below promises test orgs are excluded from every figure on this page, so the
  // rows must honour it too (the API zeroes their money but still lists them for ops screens).
  const orgs = (stats?.orgs ?? [])
    .filter((o) => !o.isTest)
    .sort((a, b) => b.revenueInrEq - a.revenueInrEq);

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Revenue</h1>
          <div className="sub">
            Credit purchases, by currency and by org · MTD · each org pays in its own currency · test
            orgs excluded · live from the API
          </div>
        </div>
        <span className="pill n">Money</span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading revenue…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load revenue. Please retry in a moment.</span>
        </div>
      )}

      {stats && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat hl">
              {/* One axis across currencies — the ₹-equivalent the API computes with FX_TO_INR. */}
              <div className="l">Revenue · MTD (₹-eq)</div>
              <div className="n violet tnum">{inrCompact(stats.revenue.inrEq)}</div>
              <div className="d">≈ {usdCompact(stats.revenue.inrEq)} · ≈ FX for rollup only</div>
            </div>
            {stats.revenue.byCurrency.map((c) => (
              <div key={c.currency} className="card pad stat">
                <div className="l">
                  {c.currency} gross · MTD <span className="tag">{CURRENCY_META[c.currency]?.symbol ?? c.currency}</span>
                </div>
                <div className="n teal tnum">{currencyMinor(c.grossMinor, c.currency)}</div>
                <div className="d">
                  {c.purchases} purchase{c.purchases === 1 ? "" : "s"} · billed in {c.currency}
                </div>
              </div>
            ))}
            {stats.revenue.byCurrency.length === 0 && (
              <div className="card pad stat">
                <div className="l">By currency</div>
                <div className="n tnum">—</div>
                <div className="d">no credit purchases yet this cycle</div>
              </div>
            )}
          </div>

          {/* Cash vs credits: the two worlds the credit model creates. Cash reconciles with the
              gateway/bank; credits are a prepaid unit that burns later (or never). */}
          {stats.money && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>Money — cash vs credits</h3>
                <span className="mut sm">MTD · test orgs excluded</span>
              </div>
              <div className="card-b">
                <div className="krow">
                  <span className="k">Cash captured MTD — gross of gateway fees</span>
                  <span className="v tnum">
                    {stats.money.cash.byCurrency.length === 0
                      ? "—"
                      : stats.money.cash.byCurrency
                          .map((c) => `${currencyMinor(c.grossMinor, c.currency)} (${c.purchases})`)
                          .join(" · ")}
                  </span>
                </div>
                <div className="krow">
                  <span className="k">
                    Credits granted free
                    {stats.money.creditsGrantedFree.adjustmentUnits > 0
                      ? ` — net of ${stats.money.creditsGrantedFree.adjustmentUnits.toLocaleString("en-IN")} cr taken back`
                      : ""}
                  </span>
                  <span className="v tnum">
                    {stats.money.creditsGrantedFree.netUnits.toLocaleString("en-IN")} cr · ≈{" "}
                    {inrCompact(stats.money.creditsGrantedFree.notionalInr)} notional at list — never cash
                  </span>
                </div>
                <div className="krow">
                  <span className="k">Credits burned</span>
                  <span className="v tnum">
                    {stats.money.creditsBurned.spendUnits.toLocaleString("en-IN")} cr spent
                    {stats.money.creditsBurned.expiryUnits > 0
                      ? ` · ${stats.money.creditsBurned.expiryUnits.toLocaleString("en-IN")} cr expired unspent`
                      : ""}
                  </span>
                </div>
                <div className="krow">
                  <span className="k">Deferred revenue — cash for credits not yet consumed</span>
                  <span className="v tnum">
                    {stats.money.deferredRevenue.byCurrency.length === 0
                      ? "—"
                      : stats.money.deferredRevenue.byCurrency
                          .map((c) => currencyMinor(c.deferredMinor, c.currency))
                          .join(" · ")}
                  </span>
                </div>
                <p className="mut sm" style={{ margin: "10px 0 0" }}>
                  Cash is the only number that should match the bank; credits are prepaid, so their
                  consumption lags the cash that bought them.
                </p>
              </div>
            </div>
          )}

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Revenue by org · this cycle</h3>
              <span className="mut sm">Click a row to drill in</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Currency</th>
                    <th>Revenue</th>
                    <th>₹-eq</th>
                    <th>COGS</th>
                    <th>Margin %</th>
                    <th>Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="mut sm">
                        No revenue yet this cycle.
                      </td>
                    </tr>
                  )}
                  {orgs.map((o) => (
                    <tr key={o.id} className="clickable" onClick={() => navigate(`/admin/orgs/${o.id}`)}>
                      <td style={{ fontWeight: 600 }}>{o.name}</td>
                      <td>
                        <span className="pill n">{o.currency}</span>
                      </td>
                      {/* Revenue in the org's OWN currency — the only figure that is real money. */}
                      <td className="tnum">{currencyMinor(o.revenueMinor, o.currency)}</td>
                      <td className="tnum">
                        {inrCompact(o.revenueInrEq)} · {usdCompact(o.revenueInrEq)}
                      </td>
                      <td className="tnum">
                        {inrCompact(o.cogsInr)} · {usdCompact(o.cogsInr)}
                      </td>
                      <td>
                        {/* null = no revenue this cycle — "—", never a fake 0%. */}
                        {o.marginPct == null ? (
                          <span className="pill n">—</span>
                        ) : (
                          <span className={o.marginPct >= 60 ? "pill v" : o.marginPct > 0 ? "pill r" : "pill n"}>
                            {o.marginPct}%
                          </span>
                        )}
                      </td>
                      <td className="tnum">{o.verified.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>Revenue = captured credit purchases</b> (the gateway ledger), shown in each org's own billing
        currency. The ₹-equivalent uses the fixed <code>FX_TO_INR</code> rollup rates — a reporting axis, never
        a billed amount. COGS comes from the cost ledger, so margin here reconciles with Spend. USD figures
        are display-only at a fixed rate. <b>Test organisations are excluded</b> from every figure on this
        page — our own demo runs are not a customer's revenue.
      </div>
    </section>
  );
}
